import { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Wifi, WifiOff } from 'lucide-react';
import aiveliqLogo from '@/assets/aiveliq360-logo.png';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/usePWA';
import { cn } from '@/lib/utils';

export function InstallPrompt() {
  const { canInstall, installPWA, isIOS, isInstalled, isOnline } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed before
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Show prompt after a delay if installable
    const timer = setTimeout(() => {
      if ((canInstall || isIOS) && !isInstalled) {
        setShowPrompt(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [canInstall, isIOS, isInstalled]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
    } else {
      const result = await installPWA();
      if (result.success) {
        setShowPrompt(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (dismissed || isInstalled || (!canInstall && !isIOS)) {
    return null;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <>
      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="h-4 w-4" />
          You're offline. Some features may be limited.
        </div>
      )}

      {/* Install prompt banner */}
      <div className={cn(
        "fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-lg border-t border-border shadow-2xl",
        "animate-in slide-in-from-bottom duration-500",
        "safe-area-inset-bottom"
      )}>
        <div className="max-w-lg mx-auto">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="flex items-start gap-4">
            <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-14 w-14 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg mb-1">Install Aiveliq360</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get quick access, offline support, and a native app experience on your device.
              </p>
              <div className="flex items-center gap-2">
                <Button onClick={handleInstall} className="gap-2">
                  <Download className="h-4 w-4" />
                  Install App
                </Button>
                <Button variant="ghost" onClick={handleDismiss}>
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* iOS Installation Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full shadow-2xl animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Install on iOS</h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-primary">1</span>
                </div>
                <div>
                  <p className="font-medium">Tap the Share button</p>
                  <p className="text-sm text-muted-foreground">
                    Find the <Share className="h-4 w-4 inline" /> icon at the bottom of Safari
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-primary">2</span>
                </div>
                <div>
                  <p className="font-medium">Scroll and tap "Add to Home Screen"</p>
                  <p className="text-sm text-muted-foreground">
                    Look for the <Plus className="h-4 w-4 inline" /> Add to Home Screen option
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-primary">3</span>
                </div>
                <div>
                  <p className="font-medium">Tap "Add"</p>
                  <p className="text-sm text-muted-foreground">
                    Confirm to add Aiveliq360 to your home screen
                  </p>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setShowIOSGuide(false)} 
              className="w-full mt-6"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
